import {
  DocumentData,
  QueryDocumentSnapshot,
  Unsubscribe,
  arrayUnion,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  runTransaction,
  updateDoc,
  where
} from "firebase/firestore";
import { User } from "firebase/auth";
import { db } from "../lib/firebase";

export type InboxState<T> = {
  items: T[];
  loading: boolean;
  error: string | null;
};

export type ActionEventSummary = {
  id: string;
  title: string;
  startDate?: number | null;
  status: string;
  isDeleted: boolean;
};

export type AthleteInvitationItem = {
  id: string;
  eventId: string;
  eventTitle: string;
  eventType: string;
  organizerId: string;
  organizerName: string;
  inviteeId: string;
  inviteeEmail: string;
  inviteeName: string;
  isExternalInvite: boolean;
  claimedAt?: number | null;
  status: string;
  isRead: boolean;
  createdAt?: number | null;
  updatedAt?: number | null;
  acceptedAt?: number | null;
  declinedAt?: number | null;
};

export type SupportControlRole = "SUPPORT" | "CONTROL";

export type SupportControlRequestItem = {
  id: string;
  eventId: string;
  eventTitle: string;
  eventEndTimeMillis: number;
  organizerId: string;
  organizerName: string;
  requestedUserId: string;
  requestedUserEmail: string;
  requestedUserName: string;
  role: SupportControlRole;
  status: string;
  createdAt?: number | null;
  updatedAt?: number | null;
  respondedAt?: number | null;
};

export type HelpRequestItem = {
  id: string;
  eventId: string;
  eventTitle: string;
  athleteId: string;
  athleteName: string;
  registryNumber: string;
  latitude: number;
  longitude: number;
  message: string;
  requestType: "SOS" | "WITHDRAWAL";
  status: string;
  createdAt?: number | null;
  updatedAt?: number | null;
  resolvedAt?: number | null;
  resolvedBy: string;
};

export type PendingActionsSummary = {
  total: number;
  invitations: number;
  supportControl: number;
  help: number;
  loading: boolean;
  error: string | null;
};

function stringValue(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function numberValue(value: unknown, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function booleanValue(value: unknown, fallback = false) {
  return typeof value === "boolean" ? value : fallback;
}

function millisValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function cleanEmail(value?: string | null) {
  return (value ?? "").trim().toLowerCase();
}

function invitationFromSnapshot(snapshot: QueryDocumentSnapshot<DocumentData>): AthleteInvitationItem {
  const data = snapshot.data();
  return {
    id: stringValue(data.id, snapshot.id),
    eventId: stringValue(data.eventId),
    eventTitle: stringValue(data.eventTitle),
    eventType: stringValue(data.eventType),
    organizerId: stringValue(data.organizerId),
    organizerName: stringValue(data.organizerName),
    inviteeId: stringValue(data.inviteeId),
    inviteeEmail: stringValue(data.inviteeEmail),
    inviteeName: stringValue(data.inviteeName),
    isExternalInvite: booleanValue(data.isExternalInvite),
    claimedAt: millisValue(data.claimedAt),
    status: stringValue(data.status, "PENDING").toUpperCase(),
    isRead: booleanValue(data.isRead),
    createdAt: millisValue(data.createdAt),
    updatedAt: millisValue(data.updatedAt),
    acceptedAt: millisValue(data.acceptedAt),
    declinedAt: millisValue(data.declinedAt)
  };
}

function supportControlFromSnapshot(snapshot: QueryDocumentSnapshot<DocumentData>): SupportControlRequestItem {
  const data = snapshot.data();
  const role = stringValue(data.role, "SUPPORT").toUpperCase() === "CONTROL" ? "CONTROL" : "SUPPORT";
  return {
    id: stringValue(data.id, snapshot.id),
    eventId: stringValue(data.eventId),
    eventTitle: stringValue(data.eventTitle),
    eventEndTimeMillis: numberValue(data.eventEndTimeMillis),
    organizerId: stringValue(data.organizerId),
    organizerName: stringValue(data.organizerName),
    requestedUserId: stringValue(data.requestedUserId),
    requestedUserEmail: stringValue(data.requestedUserEmail),
    requestedUserName: stringValue(data.requestedUserName),
    role,
    status: stringValue(data.status, "PENDING").toUpperCase(),
    createdAt: millisValue(data.createdAt),
    updatedAt: millisValue(data.updatedAt),
    respondedAt: millisValue(data.respondedAt)
  };
}

function helpRequestFromSnapshot(snapshot: QueryDocumentSnapshot<DocumentData>): HelpRequestItem {
  const data = snapshot.data();
  const requestType = stringValue(data.requestType, "SOS").toUpperCase() === "WITHDRAWAL" ? "WITHDRAWAL" : "SOS";
  return {
    id: stringValue(data.id, snapshot.id),
    eventId: stringValue(data.eventId),
    eventTitle: stringValue(data.eventTitle),
    athleteId: stringValue(data.athleteId),
    athleteName: stringValue(data.athleteName),
    registryNumber: stringValue(data.registryNumber),
    latitude: numberValue(data.latitude),
    longitude: numberValue(data.longitude),
    message: stringValue(data.message),
    requestType,
    status: stringValue(data.status, "OPEN").toUpperCase(),
    createdAt: millisValue(data.createdAt),
    updatedAt: millisValue(data.updatedAt),
    resolvedAt: millisValue(data.resolvedAt),
    resolvedBy: stringValue(data.resolvedBy)
  };
}

function eventSummaryFromData(id: string, data: DocumentData): ActionEventSummary {
  return {
    id,
    title: stringValue(data.title, "Δράση PULS"),
    startDate: millisValue(data.startDate),
    status: stringValue(data.status, "UPCOMING"),
    isDeleted: booleanValue(data.isDeleted)
  };
}

function mergeInbox<T extends { id: string; createdAt?: number | null }>(
  first: T[],
  second: T[]
) {
  const merged = new Map<string, T>();
  [...first, ...second].forEach((item) => merged.set(item.id, item));
  return [...merged.values()].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
}

export function subscribeMyAthleteInvitations(
  user: User,
  callback: (state: InboxState<AthleteInvitationItem>) => void
): Unsubscribe {
  callback({ items: [], loading: true, error: null });

  const knownQuery = query(collection(db, "invitations"), where("inviteeId", "==", user.uid));
  const externalEmail = cleanEmail(user.email);
  const externalQuery = externalEmail
    ? query(
        collection(db, "invitations"),
        where("inviteeEmail", "==", externalEmail),
        where("status", "==", "PENDING"),
        where("isExternalInvite", "==", true),
        where("inviteeId", "==", "")
      )
    : null;

  let knownItems: AthleteInvitationItem[] = [];
  let externalItems: AthleteInvitationItem[] = [];
  let knownLoaded = false;
  let externalLoaded = externalQuery === null;
  let stopped = false;

  const emit = (error: string | null = null) => {
    if (stopped) return;
    callback({
      items: mergeInbox(knownItems, externalItems),
      loading: !(knownLoaded && externalLoaded),
      error
    });
  };

  const unsubscribers: Unsubscribe[] = [];
  unsubscribers.push(
    onSnapshot(
      knownQuery,
      (snapshot) => {
        knownItems = snapshot.docs.map(invitationFromSnapshot);
        knownLoaded = true;
        emit();
      },
      (error) => {
        knownLoaded = true;
        emit(error.message);
      }
    )
  );

  if (externalQuery) {
    unsubscribers.push(
      onSnapshot(
        externalQuery,
        (snapshot) => {
          externalItems = snapshot.docs
            .map(invitationFromSnapshot)
            .filter((invitation) => invitation.inviteeId === "" || invitation.inviteeId === user.uid);
          externalLoaded = true;
          emit();
        },
        (error) => {
          externalLoaded = true;
          emit(error.message);
        }
      )
    );
  }

  return () => {
    stopped = true;
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
}

export function subscribeMySupportControlRequests(
  userId: string,
  callback: (state: InboxState<SupportControlRequestItem>) => void
): Unsubscribe {
  callback({ items: [], loading: true, error: null });

  const incomingQuery = query(collection(db, "supportControlRequests"), where("requestedUserId", "==", userId));
  return onSnapshot(
    incomingQuery,
    (snapshot) => {
      const now = Date.now();
      const items = snapshot.docs
        .map(supportControlFromSnapshot)
        .filter((request) => request.eventEndTimeMillis <= 0 || request.eventEndTimeMillis > now)
        .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
      callback({ items, loading: false, error: null });
    },
    (error) => {
      callback({ items: [], loading: false, error: error.message });
    }
  );
}

export function subscribeMyHelpRequests(
  userId: string,
  callback: (state: InboxState<HelpRequestItem>) => void
): Unsubscribe {
  callback({ items: [], loading: true, error: null });

  const helpQuery = query(collection(db, "eventHelpRequests"), where("targetUserIds", "array-contains", userId));
  return onSnapshot(
    helpQuery,
    (snapshot) => {
      const items = snapshot.docs
        .map(helpRequestFromSnapshot)
        .filter((request) => request.status === "OPEN")
        .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
      callback({ items, loading: false, error: null });
    },
    (error) => {
      callback({ items: [], loading: false, error: error.message });
    }
  );
}

export function subscribeMyPendingActionsSummary(
  user: User,
  callback: (summary: PendingActionsSummary) => void
): Unsubscribe {
  let invitationState: InboxState<AthleteInvitationItem> = { items: [], loading: true, error: null };
  let supportState: InboxState<SupportControlRequestItem> = { items: [], loading: true, error: null };
  let helpState: InboxState<HelpRequestItem> = { items: [], loading: true, error: null };

  const emit = () => {
    const invitations = invitationState.items.filter((invitation) => invitation.status === "PENDING").length;
    const supportControl = supportState.items.filter((request) => request.status === "PENDING").length;
    const help = helpState.items.filter((request) => request.status === "OPEN").length;
    const errors = [invitationState.error, supportState.error, helpState.error].filter(Boolean);

    callback({
      total: invitations + supportControl + help,
      invitations,
      supportControl,
      help,
      loading: invitationState.loading || supportState.loading || helpState.loading,
      error: errors.length > 0 ? errors.join(" · ") : null
    });
  };

  const unsubscribers = [
    subscribeMyAthleteInvitations(user, (state) => {
      invitationState = state;
      emit();
    }),
    subscribeMySupportControlRequests(user.uid, (state) => {
      supportState = state;
      emit();
    }),
    subscribeMyHelpRequests(user.uid, (state) => {
      helpState = state;
      emit();
    })
  ];

  return () => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
}

export async function fetchActionEventSummaries(eventIds: string[]) {
  const uniqueIds = [...new Set(eventIds.map((id) => id.trim()).filter(Boolean))];
  const pairs = await Promise.all(
    uniqueIds.map(async (eventId) => {
      const snapshot = await getDoc(doc(db, "events", eventId));
      if (!snapshot.exists()) return [eventId, null] as const;
      return [eventId, eventSummaryFromData(snapshot.id, snapshot.data())] as const;
    })
  );

  return pairs.reduce<Record<string, ActionEventSummary | null>>((accumulator, [eventId, summary]) => {
    accumulator[eventId] = summary;
    return accumulator;
  }, {});
}

async function claimExternalInvitationIfNeeded(invitation: AthleteInvitationItem, user: User) {
  if (!invitation.isExternalInvite || invitation.inviteeId || invitation.status !== "PENDING") return;
  const email = cleanEmail(user.email);
  if (!email || cleanEmail(invitation.inviteeEmail) !== email) return;

  const now = Date.now();
  await updateDoc(doc(db, "invitations", invitation.id), {
    inviteeId: user.uid,
    inviteeEmail: email,
    claimedAt: now,
    updatedAt: now,
    isRead: true
  });
}

export async function respondToInvitation(
  invitation: AthleteInvitationItem,
  response: "ACCEPTED" | "DECLINED",
  user: User
) {
  await claimExternalInvitationIfNeeded(invitation, user);

  if (response === "DECLINED") {
    const now = Date.now();
    await updateDoc(doc(db, "invitations", invitation.id), {
      status: "DECLINED",
      isRead: true,
      declinedAt: now,
      updatedAt: now
    });
    return null;
  }

  return runTransaction(db, async (transaction) => {
    const invitationRef = doc(db, "invitations", invitation.id);
    const invitationSnapshot = await transaction.get(invitationRef);
    if (!invitationSnapshot.exists()) throw new Error("invitation-not-found");
    const freshInvitation = invitationFromSnapshot(invitationSnapshot as QueryDocumentSnapshot<DocumentData>);
    if (freshInvitation.inviteeId !== user.uid) throw new Error("invitation-no-access");
    if (freshInvitation.status !== "PENDING") throw new Error("invitation-not-pending");

    const eventRef = doc(db, "events", freshInvitation.eventId);
    const eventSnapshot = await transaction.get(eventRef);
    if (!eventSnapshot.exists()) throw new Error("event-not-found");
    const eventData = eventSnapshot.data();
    const participantIds = stringList(eventData.participantIds);
    const maxParticipants = numberValue(eventData.maxParticipants, 0);
    const eventType = stringValue(eventData.eventType);

    const userRef = doc(db, "users", user.uid);
    const userSnapshot = await transaction.get(userRef);
    const userData = userSnapshot.data() ?? {};
    const displayName = stringValue(userData.displayName, user.displayName ?? "Αθλητής");
    const userEmail = stringValue(userData.email, user.email ?? "");
    const registryNumber = stringValue(userData.cyclistRegistryNumber).trim();
    const now = Date.now();

    if (eventType === "BREVET" && !registryNumber) throw new Error("cyclist-profile-required");

    if (!participantIds.includes(user.uid)) {
      if (maxParticipants > 0 && participantIds.length >= maxParticipants) throw new Error("event-full");
      const nextParticipantIds = [...participantIds, user.uid];
      transaction.update(eventRef, {
        participantIds: nextParticipantIds,
        currentParticipants: nextParticipantIds.length
      });
    }

    if (eventType === "BREVET") {
      transaction.set(doc(db, "events", freshInvitation.eventId, "participants", user.uid), {
        eventId: freshInvitation.eventId,
        userId: user.uid,
        displayName,
        email: userEmail,
        phoneNumber: stringValue(userData.phoneNumber),
        registryNumber,
        bikeBrand: stringValue(userData.bikeBrand),
        bikeModel: stringValue(userData.bikeModel),
        emergencyContactName: stringValue(userData.emergencyContactName),
        emergencyContactPhone: stringValue(userData.emergencyContactPhone),
        joinedAt: now,
        updatedAt: now
      });

      transaction.set(doc(db, "events", freshInvitation.eventId, "operationalParticipants", user.uid), {
        eventId: freshInvitation.eventId,
        userId: user.uid,
        displayName,
        registryNumber,
        phoneNumber: stringValue(userData.phoneNumber),
        joinedAt: now,
        updatedAt: now
      });
    }

    transaction.set(doc(db, "events", freshInvitation.eventId, "publicParticipants", user.uid), {
      eventId: freshInvitation.eventId,
      userId: user.uid,
      displayName,
      joinedAt: now,
      updatedAt: now
    });

    transaction.update(invitationRef, {
      status: "ACCEPTED",
      isRead: true,
      acceptedAt: now,
      updatedAt: now
    });

    return freshInvitation.eventId;
  });
}

export async function respondToSupportControlRequest(
  request: SupportControlRequestItem,
  response: "ACCEPTED" | "DECLINED",
  userId: string
) {
  await runTransaction(db, async (transaction) => {
    const rootRef = doc(db, "supportControlRequests", request.id);
    const rootSnapshot = await transaction.get(rootRef);
    if (!rootSnapshot.exists()) throw new Error("request-not-found");
    const freshRequest = supportControlFromSnapshot(rootSnapshot as QueryDocumentSnapshot<DocumentData>);
    if (freshRequest.requestedUserId !== userId) throw new Error("request-no-access");
    if (freshRequest.status !== "PENDING") throw new Error("request-not-pending");

    const now = Date.now();
    const statusUpdates = {
      status: response,
      updatedAt: now,
      respondedAt: now
    };
    transaction.update(rootRef, statusUpdates);
    transaction.update(doc(db, "events", freshRequest.eventId, "supportControlRequests", freshRequest.id), statusUpdates);

    if (response === "ACCEPTED") {
      const eventRef = doc(db, "events", freshRequest.eventId);
      transaction.update(eventRef, {
        [freshRequest.role === "CONTROL" ? "controlUserIds" : "supportUserIds"]: arrayUnion(userId),
        updatedAt: now
      });
    }
  });
}

export async function resolveHelpRequest(request: HelpRequestItem, userId: string) {
  const now = Date.now();
  await updateDoc(doc(db, "eventHelpRequests", request.id), {
    status: "RESOLVED",
    resolvedAt: now,
    resolvedBy: userId,
    updatedAt: now
  });
}
